"use client";

import { useCallback, useMemo, useState } from "react";
import {
	Bar,
	Line,
	ComposedChart,
	CartesianGrid,
	XAxis,
	YAxis,
} from "recharts";

import {
	ChartStyleSelector,
	useChartStyle,
} from "@/components/analytics/chart-style";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/lib/components/card";
import {
	ChartContainer,
	ChartTooltip,
	ChartTooltipContent,
} from "@/lib/components/chart";
import { cn } from "@/lib/utils";

import {
	formatBucketLabel,
	formatBucketLabelWithZone,
	useDisplayTimeZone,
} from "@llmgateway/shared";
import {
	formatCompactNumber,
	formatNumber,
} from "@llmgateway/shared/number-format";

import {
	buildDimensionTimeseries,
	currencyFormatter,
	sanitizeKey,
	seriesColors,
	type ChartMetric,
	type DimensionRow,
} from "./chart-helpers";

import type { ChartConfig } from "@/lib/components/chart";

const metricTabs: { key: ChartMetric; label: string }[] = [
	{ key: "cost", label: "Cost" },
	{ key: "requestCount", label: "Requests" },
	{ key: "totalTokens", label: "Tokens" },
];

interface DimensionUsageOverTimeCardProps {
	rows: DimensionRow[];
	loading?: boolean;
	title: string;
	description: string;
}

export function DimensionUsageOverTimeCard({
	rows,
	loading = false,
	title,
	description,
}: DimensionUsageOverTimeCardProps) {
	const { style } = useChartStyle();
	const [activeMetric, setActiveMetric] = useState<ChartMetric>("cost");
	const { timeZone: displayTimeZone } = useDisplayTimeZone();

	const series = useMemo(
		() => buildDimensionTimeseries(rows, activeMetric),
		[rows, activeMetric],
	);

	const { chartData, config, keyToLabel } = useMemo(() => {
		const labelMap = new Map<string, string>();
		const cfg: ChartConfig = {};
		series.series.forEach((s, index) => {
			const key = sanitizeKey(s.key);
			labelMap.set(key, s.label);
			cfg[key] = {
				label: s.label,
				color: seriesColors[index % seriesColors.length],
			};
		});
		const data = series.data.map((point) => {
			const row: Record<string, number | string> = {
				timestamp: point.timestamp,
			};
			for (const s of series.series) {
				row[sanitizeKey(s.key)] = 0;
			}
			for (const [key, value] of Object.entries(point.entries)) {
				row[sanitizeKey(key)] = Number(value[activeMetric] ?? 0);
			}
			return row;
		});
		return { chartData: data, config: cfg, keyToLabel: labelMap };
	}, [series, activeMetric]);

	const hasData = series.series.length > 0;

	const formatTimestamp = useCallback(
		(ts: string) => formatBucketLabel(ts, "monthDay"),
		[],
	);

	return (
		<Card>
			<CardHeader className="space-y-4 pb-2">
				<div>
					<CardTitle className="text-base">{title}</CardTitle>
					<CardDescription>{description}</CardDescription>
				</div>
				<div className="flex items-center gap-1 border-b pb-2">
					{metricTabs.map((tab) => (
						<button
							key={tab.key}
							type="button"
							className={cn(
								"rounded-md px-3 py-1 text-xs font-medium transition-colors",
								activeMetric === tab.key
									? "bg-primary text-primary-foreground"
									: "text-muted-foreground hover:text-foreground",
							)}
							onClick={() => setActiveMetric(tab.key)}
						>
							{tab.label}
						</button>
					))}
				</div>
				<ChartStyleSelector />
			</CardHeader>
			<CardContent className="px-2 pb-4 sm:px-6">
				{loading ? (
					<div className="flex h-[300px] items-center justify-center text-sm text-muted-foreground">
						Loading…
					</div>
				) : !hasData ? (
					<div className="flex h-[300px] items-center justify-center text-sm text-muted-foreground">
						No data for this time period
					</div>
				) : (
					<>
						<ChartContainer
							config={config}
							className="aspect-auto h-[300px] w-full"
						>
							<ComposedChart
								data={chartData}
								margin={{ left: 0, right: 8, top: 4, bottom: 0 }}
							>
								<CartesianGrid vertical={false} strokeDasharray="3 3" />
								<XAxis
									dataKey="timestamp"
									tickLine={false}
									axisLine={false}
									tickMargin={8}
									minTickGap={40}
									tickFormatter={(value: string) => formatTimestamp(value)}
								/>
								<YAxis
									tickLine={false}
									axisLine={false}
									tickMargin={4}
									width={60}
									tickFormatter={(value: number) => {
										if (activeMetric === "cost") {
											return `$${value >= 1 ? value.toFixed(2) : value.toFixed(4)}`;
										}
										return formatCompactNumber(value);
									}}
								/>
								<ChartTooltip
									content={(props) => {
										const visiblePayload = [...(props.payload ?? [])].filter(
											(item) => Number(item.value ?? 0) > 0,
										);
										return (
											<ChartTooltipContent
												active={props.active}
												label={props.label}
												payload={visiblePayload}
												labelFormatter={(value: string) =>
													formatBucketLabelWithZone(
														value,
														"monthDayYear",
														displayTimeZone,
													)
												}
												formatter={(value, name) => {
													const label =
														keyToLabel.get(name as string) ?? String(name);
													const formatted =
														activeMetric === "cost"
															? currencyFormatter.format(Number(value))
															: formatNumber(Number(value));
													return (
														<span>
															{label}: <strong>{formatted}</strong>
														</span>
													);
												}}
											/>
										);
									}}
								/>
								{series.series.map((s) => {
									const key = sanitizeKey(s.key);
									return style === "bar" ? (
										<Bar
											key={key}
											dataKey={key}
											stackId="1"
											fill={`var(--color-${key})`}
											isAnimationActive={false}
										/>
									) : (
										<Line
											key={key}
											dataKey={key}
											type="linear"
											stroke={`var(--color-${key})`}
											strokeWidth={2}
											dot={false}
											isAnimationActive={false}
										/>
									);
								})}
							</ComposedChart>
						</ChartContainer>
						<div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-xs">
							{series.series.map((s, i) => (
								<div
									key={s.key}
									className="flex items-center gap-1.5 text-muted-foreground"
								>
									<span
										className="inline-block h-2.5 w-2.5 rounded-sm"
										style={{
											backgroundColor: seriesColors[i % seriesColors.length],
										}}
									/>
									<span className="truncate">{s.label}</span>
								</div>
							))}
						</div>
					</>
				)}
			</CardContent>
		</Card>
	);
}
